import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Play, Square, AlertCircle, Terminal as TerminalIcon, BarChart2, Sliders, Settings } from 'lucide-react';
import { getWorkflow } from '../api/workflows';
import { runBacktestSse } from '../api/backtest';
import { toBackendDag } from '../utils/graphAdapters';

export default function BacktestPage() {
  const { workflowId } = useParams();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  
  const [logs, setLogs] = useState([]);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const abortRef = useRef(null);

  const [assetType, setAssetType] = useState(null);
  const [activeDataNode, setActiveDataNode] = useState(null);
  const [overrideDataParams, setOverrideDataParams] = useState(false);

  const [config, setConfig] = useState({
      symbol: '',
      timeframe: '1h',
      limit: 500,
      exchange: 'binance',
      market: 'us',
      initial_capital: 10000,
      commission_rate: 0.001,
  });

  useEffect(() => {
    async function loadData() {
      if (!workflowId) return;
      try {
        const wf = await getWorkflow(workflowId);
        const gd = wf?.graph_data ?? {}
        if (gd?.version === 12 && Array.isArray(gd?.nodes) && Array.isArray(gd?.edges)) {
          setNodes(gd.nodes)
          setEdges(gd.edges)
        } else {
          const backendDag = toBackendDag({ nodes: gd?.nodes ?? [], edges: gd?.edges ?? [] })
          setNodes(backendDag.nodes)
          setEdges(backendDag.edges)
        }
      } catch (err) {
        console.error("Failed to load workflow", err);
      }
    }
    loadData();
  }, [workflowId]);

  useEffect(() => {
      let foundType = null;
      let foundNode = null;

      const stockNode = nodes.find((n) => n?.type === 'data.fetch_stock');
      if (stockNode) {
          foundType = 'stock';
          foundNode = stockNode;
      } else {
          const cryptoNode = nodes.find((n) => n?.type === 'data.fetch_crypto');
          if (cryptoNode) {
              foundType = 'crypto';
              foundNode = cryptoNode;
          }
      }

      setAssetType(foundType);
      setActiveDataNode(foundNode);

      if (foundNode && !overrideDataParams) {
          const d = foundNode.data || {};
          setConfig(prev => ({
              ...prev,
              symbol: d.symbol || prev.symbol || (foundType === 'crypto' ? 'BTC/USDT' : 'AAPL'),
              timeframe: d.timeframe || prev.timeframe,
              limit: d.limit || prev.limit,
              exchange: d.exchange || prev.exchange,
              market: d.market || prev.market,
          }));
      }
  }, [nodes, overrideDataParams]);

  async function handleRun() {
    if (!workflowId) return;
    if (abortRef.current) {
        abortRef.current.abort();
    }
    
    setIsRunning(true);
    setLogs([]);
    setResult(null);
    setError(null);
    
    const abort = new AbortController();
    abortRef.current = abort;

    const runConfig = {
        initial_capital: Number(config.initial_capital),
        commission_rate: Number(config.commission_rate),
    };

    if (overrideDataParams) {
        runConfig.symbol = config.symbol;
        runConfig.timeframe = config.timeframe;
        runConfig.limit = Number(config.limit);
        
        if (assetType === 'stock') {
            runConfig.market = config.market;
        } else if (assetType === 'crypto') {
            runConfig.exchange = config.exchange;
        }
    }

    try {
        await runBacktestSse(
            {
                session_id: workflowId,
                graph_json: { nodes, edges },
                config: runConfig,
            },
            {
                signal: abort.signal,
                onEvent: (evt) => {
                    if (evt.type === 'log') {
                        setLogs((prev) => [...prev, evt.content ?? JSON.stringify(evt)]);
                    } else if (evt.type === 'error') {
                        setError(evt.content ?? 'Unknown error');
                    } else if (evt.type === 'result') {
                        setResult(evt);
                    } else {
                        setLogs((prev) => [...prev, typeof evt === 'string' ? evt : JSON.stringify(evt)]);
                    }
                }
            }
        );
    } catch (e) {
        if (e.name !== 'AbortError') {
             setError(e.message);
        }
    } finally {
        setIsRunning(false);
        abortRef.current = null;
    }
  }

  function handleStop() {
      if (abortRef.current) {
          abortRef.current.abort();
          setIsRunning(false);
          setLogs((prev) => [...prev, 'Backtest stopped by user.']);
      }
  }

  const isConfigDisabled = !workflowId || !assetType;

  return (
    <div className="h-full flex flex-col">
       <header className="h-14 border-b border-[var(--border-subtle)] bg-[var(--bg-panel)] flex items-center justify-between px-6">
            <h2 className="font-semibold text-sm flex items-center gap-2">
                <BarChart2 size={18} className="text-[var(--accent-green)]"/>
                Backtest Runner
            </h2>
            <div className="flex items-center gap-3">
                {isRunning ? (
                    <button onClick={handleStop} className="btn btn-ghost text-[var(--accent-red)] border-[var(--accent-red)]/20">
                        <Square size={16} fill="currentColor" /> Stop
                    </button>
                ) : (
                    <button 
                        onClick={handleRun} 
                        disabled={isConfigDisabled}
                        className="btn btn-primary bg-[var(--accent-green)] hover:bg-[#238636] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Play size={16} fill="currentColor" /> Run Backtest
                    </button>
                )}
            </div>
       </header>

       <div className="flex-1 flex overflow-hidden">
           <div className="flex-1 flex flex-col bg-[var(--bg-app)] overflow-hidden">
               {error && (
                   <div className="m-4 p-3 bg-[var(--accent-red-dim)] border border-[var(--accent-red)]/30 rounded-lg flex items-center gap-3 text-sm text-[var(--accent-red)]">
                       <AlertCircle size={20} />
                       {error}
                   </div>
               )}

                {!assetType && !error && nodes.length > 0 && (
                    <div className="m-4 p-3 bg-[var(--accent-red-dim)] border border-[var(--accent-red)]/30 rounded-lg flex items-center gap-3 text-sm text-[var(--accent-red)]">
                        <AlertCircle size={20} />
                        No valid data node found. Please add a `data.fetch_stock` or `data.fetch_crypto` node.
                    </div>
                )}

                {result?.metrics ? (
                    <div className="p-6 grid grid-cols-3 gap-4">
                        <MetricCard label="Total Return" value={formatPct(result.metrics.total_return)} />
                        <MetricCard label="Sharpe Ratio" value={formatNum(result.metrics.sharpe_ratio)} />
                        <MetricCard label="Max Drawdown" value={formatPct(result.metrics.max_drawdown)} />
                        <MetricCard label="Win Rate" value={formatPct(result.metrics.win_rate)} />
                        <MetricCard label="Trades" value={result.metrics.total_trades} />
                        <MetricCard label="Profit Factor" value={formatNum(result.metrics.profit_factor)} />
                    </div>
                ) : (
                   !isRunning && !logs.length && !error && (
                       <div className="flex-1 flex flex-col items-center justify-center text-[var(--text-secondary)]">
                           <BarChart2 size={48} className="mb-4 opacity-20" />
                           <p>Run a backtest to see results</p>
                       </div>
                   )
                )}

               <div className="flex-1 border-t border-[var(--border-subtle)] flex flex-col min-h-[300px]">
                   <div className="h-9 px-4 flex items-center gap-2 bg-[var(--bg-panel)] border-b border-[var(--border-subtle)]">
                       <TerminalIcon size={14} className="text-[var(--text-secondary)]" />
                       <span className="text-xs font-medium text-[var(--text-secondary)]">Execution Logs</span>
                   </div>
                   <div className="flex-1 bg-[#0d1117] p-4 font-mono text-xs overflow-y-auto">
                        {logs.map((line, i) => (
                            <div key={i} className="mb-1 break-words">
                                <span className="text-[var(--text-secondary)]">[{new Date().toLocaleTimeString()}]</span>{' '}
                                <span className="text-[var(--text-primary)]">{String(line)}</span>
                            </div>
                        ))}
                        {isRunning && <div className="animate-pulse text-[var(--accent-green)]">_</div>}
                    </div>
                </div>
            </div>

           <div className="w-[360px] border-l border-[var(--border-subtle)] bg-[var(--bg-panel)] flex flex-col transition-all duration-300">
                <div className="flex items-center p-1 mx-3 mt-3 mb-2 bg-[var(--bg-input)] border border-[var(--border-subtle)] rounded-lg">
                    <button 
                        className="flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium transition-all duration-200 rounded-md text-[var(--accent-blue)] shadow-sm"
                        style={{ backgroundColor: 'rgba(47, 129, 247, 0.22)' }}
                    >
                        <Settings size={16} className="text-[var(--accent-blue)]"/>
                        Configuration
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-6">
                    
                    <div>
                        <h3 className="text-xs font-bold text-[var(--text-secondary)] uppercase mb-3 flex items-center gap-2">
                            <Sliders size={12} /> Engine Params
                        </h3>
                        <div className="space-y-3">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs text-[var(--text-secondary)]">Initial Capital</label>
                                <input
                                     type="number"
                                     className="input"
                                     value={config.initial_capital}
                                     onChange={(e) => setConfig({ ...config, initial_capital: Number(e.target.value) })}
                                 />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs text-[var(--text-secondary)]">Commission Rate</label>
                                 <input
                                     type="number"
                                     step="0.0001"
                                     className="input"
                                     value={config.commission_rate}
                                     onChange={(e) => setConfig({ ...config, commission_rate: Number(e.target.value) })}
                                 />
                            </div>
                        </div>
                    </div>

                    <div className="h-px bg-[var(--border-subtle)]" />

                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-xs font-bold text-[var(--text-secondary)] uppercase flex items-center gap-2">
                                <BarChart2 size={12} /> Data Params
                            </h3>
                            <div className="flex items-center gap-2">
                                <label className="text-[10px] uppercase text-[var(--text-secondary)] font-semibold cursor-pointer select-none" htmlFor="override-toggle">Override</label>
                                <input 
                                    id="override-toggle"
                                    type="checkbox"
                                    checked={overrideDataParams}
                                    onChange={(e) => setOverrideDataParams(e.target.checked)}
                                    disabled={!assetType}
                                    className="accent-[var(--accent-blue)] w-4 h-4 cursor-pointer"
                                />
                            </div>
                        </div>

                        {!assetType ? (
                            <div className="text-xs text-[var(--text-secondary)] italic">
                                No asset node detected.
                            </div>
                        ) : (
                            <div className={`space-y-3 ${!overrideDataParams ? 'opacity-60' : ''}`}>
                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-[var(--text-secondary)]">Asset Type</label>
                                    <div className="text-xs font-mono px-2 py-1.5 bg-[var(--bg-app)] border border-[var(--border-subtle)] rounded text-[var(--text-primary)] uppercase">
                                        {assetType}
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-[var(--text-secondary)]">Symbol</label>
                                     <input 
                                        className="input" 
                                        value={config.symbol}
                                        readOnly={!overrideDataParams}
                                        onChange={e => setConfig({...config, symbol: e.target.value})}
                                     />
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-[var(--text-secondary)]">Timeframe</label>
                                     <select 
                                        className="input"
                                        value={config.timeframe}
                                        disabled={!overrideDataParams}
                                        onChange={e => setConfig({...config, timeframe: e.target.value})}
                                     >
                                        <option value="1m">1 Minute</option>
                                        <option value="5m">5 Minutes</option>
                                        <option value="15m">15 Minutes</option>
                                        <option value="1h">1 Hour</option>
                                        <option value="4h">4 Hours</option>
                                        <option value="1d">1 Day</option>
                                    </select>
                                </div>

                                {assetType === 'crypto' && (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-[var(--text-secondary)]">Exchange</label>
                                         <input 
                                            className="input" 
                                            value={config.exchange}
                                            readOnly={!overrideDataParams}
                                            onChange={e => setConfig({...config, exchange: e.target.value})}
                                         />
                                    </div>
                                )}

                                {assetType === 'stock' && (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-xs text-[var(--text-secondary)]">Market</label>
                                         <input 
                                            className="input" 
                                            value={config.market}
                                            readOnly={!overrideDataParams}
                                            onChange={e => setConfig({...config, market: e.target.value})}
                                            placeholder="us"
                                         />
                                    </div>
                                )}

                                <div className="flex flex-col gap-1">
                                    <label className="text-xs text-[var(--text-secondary)]">Limit</label>
                                     <input
                                         type="number"
                                         className="input"
                                         value={config.limit}
                                         readOnly={!overrideDataParams}
                                         onChange={(e) => setConfig({ ...config, limit: Number(e.target.value) })}
                                     />
                                </div>
                            </div>
                        )}
                        {!overrideDataParams && assetType && (
                            <div className="mt-2 text-[11px] text-[var(--text-secondary)]">
                                * Using parameters defined in the <strong>{assetType}</strong> node. Toggle override to change.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
     </div>
   );
}

function MetricCard({ label, value }) {
    return (
        <div className="p-4 bg-[var(--bg-panel)] border border-[var(--border-subtle)] rounded-lg">
            <div className="text-xs text-[var(--text-secondary)] mb-1">{label}</div>
            <div className="text-xl font-bold font-mono">{value ?? '-'}</div>
        </div>
    );
}

function formatNum(n) {
  if (n === null || n === undefined) return '-';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return num.toFixed(2);
}

function formatPct(n) {
  if (n === null || n === undefined) return '-';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return `${num.toFixed(2)}%`;
}
